import { api, paged } from '../console/api.ts';
import { type CarrierView, carrierName } from '../console/carriers.ts';
import { empty, field, loadMore, meter, pageHead, pill } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import {
  cap,
  date,
  esc,
  fmt,
  icon,
  type Money,
  PRODUCT,
  person,
  STATUS,
  when,
} from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { supervises } from '../console/session.ts';
import { $, act, centsOf, textInput } from '../console/ui.ts';
import {
  dateControl,
  optionTag,
  selectControl,
  textareaControl,
  textControl,
} from '../controls.ts';

/**
 * «Órdenes de trabajo»: what the carriers work by hand (products without an online quoter or
 * issuer, endorsements, changes, group policies, agent codes): its stage, who answers and how
 * much is left against the agreed time. The direction completes an order with what the carrier
 * gave back.
 */

/** The breakdown and installments of a premium in firm, as the carrier's desk states them. */
const FIRM_FIELDS: [key: string, label: string, placeholder: string][] = [
  ['net', 'Prima neta', '12,347.88'],
  ['fee', 'Gastos de emisión', '75.00'],
  ['surcharge', 'Recargo por fraccionamiento', '987.83'],
  ['vat', 'IVA', '1,609.29'],
  ['gross', 'Prima total', '15,020.00'],
  ['count', 'Número de recibos', '12'],
  ['first', 'Primer recibo', '1,251.74'],
  ['regular', 'Cada recibo', '1,251.66'],
  ['last', 'Último recibo', '1,251.66'],
];
/**
 * Completing an issuance: when the carrier issued with another premium than the order's (the
 * indicative one), the desk records it with its breakdown and installment plan.
 */
const firmPremiumFields = (id: string, carried: Money, indicative: boolean) =>
  `<details class="firm-premium"><summary>¿La aseguradora emitió con otra prima? La ${indicative ? 'indicativa' : 'de la orden'} es ${esc(fmt(carried))}</summary>
    <p class="muted small">Escríbela como la dio la aseguradora: la póliza, sus recibos y la comisión usan la prima en firme, y Nubi avisa a la agente de la diferencia.</p>
    <div class="firm-grid">${FIRM_FIELDS.map(([k, label, ph]) =>
      field(
        label,
        textControl({
          class: 'num',
          [`data-order-firm-${k}`]: id,
          inputmode: k === 'count' ? 'numeric' : 'decimal',
          placeholder: ph,
        }),
      ),
    ).join('')}</div></details>`;
/** «Prima en firme Q15,020.00 · Q449.83 más que la indicativa». */
const firmPremiumText = (f: NonNullable<DeskTaskView['firmPremium']>) => {
  const d = f.difference?.amountMinor ?? 0;
  const diff = d
    ? ` · ${fmt({ amountMinor: Math.abs(d), currency: f.gross.currency })} ${d > 0 ? 'más' : 'menos'} que la indicativa`
    : '';
  return `Prima en firme ${fmt(f.gross)} en ${f.installments.count} ${f.installments.count === 1 ? 'recibo' : 'recibos'}${diff}`;
};

type DeskTaskView = {
  id: string;
  carrier: string;
  kind: string;
  /** What the carrier gave back on completion (the SIB registration number). */
  reference: string | null;
  /** What the carrier's underwriter decided (underwriting orders). */
  resolution: { decision: string; surchargePercent: number | null; exclusions: string[] } | null;
  subject: { type: string; id: string } | null;
  /** Opened by Nubi because the carrier has no integration for the operation (ADR-0006). */
  automatic?: boolean;
  /** What Nubi would have sent (a policy order carries the policy's currency). */
  request?: {
    currency?: string;
    /** An order for an agent's code: the product lines to state the commission of. */
    productLines?: string[];
    provisionalAgentId?: string | null;
    /** An issuance: the price Nubi priced it with (indicative when a RuleSet computed it). */
    issuance?: { premium?: { gross: Money } | null; ruleSet?: { version: number } | null } | null;
  } | null;
  /** The premium in firm an issuance was completed with, and its difference with the order's. */
  firmPremium?: {
    gross: Money;
    installments: { count: number };
    indicative: Money | null;
    difference: Money | null;
  } | null;
  /** The agent's code and commission per line, when an order for an agent's code completed. */
  agentTerms?: { agentId: string; commissions: Record<string, string>; regime: string } | null;
  /** The money the carrier stated completing an order on a policy. */
  amounts?: { total: Money | null; net: Money | null; commission: Money | null } | null;
  /** What a movement on a group policy carries (group kinds only). */
  movement?: GroupMovementView | null;
  policyNumber: string | null;
  agentId: string | null;
  title: string;
  detail: string;
  slaHours: number;
  stage: string;
  assignee: string | null;
  createdAt: string;
  dueAt: string;
  closedAt: string | null;
  elapsedHours: number;
  overdueHours: number;
  history: {
    stage: string;
    at: string;
    by: string;
    assignee: string | null;
    note: string | null;
  }[];
};
/**
 * An order Nubi opened because the carrier endorses or cancels a policy by hand: completing it
 * registers the endorsement's premium or the cancellation in billing and commissions.
 */
const followsPolicy = (t: DeskTaskView): 'ENDORSE' | 'CANCEL' | null =>
  t.automatic && t.subject?.type === 'POLICY' && (t.kind === 'ENDORSE' || t.kind === 'CANCEL')
    ? t.kind
    : null;
const KIND: Record<string, string> = {
  QUOTE: 'Cotización',
  ISSUE: 'Emisión',
  ENDORSE: 'Endoso',
  RENEW: 'Renovación',
  CANCEL: 'Cancelación',
  CHANGE: 'Modificación',
  REGULATOR_FILING: 'Registro ante la SIB',
  UNDERWRITING: 'Suscripción',
  APPOINTMENT: 'Código de agente',
  GROUP_ISSUE: 'Emisión colectiva',
  GROUP_INCLUSION: 'Inclusión en colectiva',
  GROUP_EXCLUSION: 'Exclusión de colectiva',
  GROUP_CANCELLATION: 'Cancelación de certificados',
  OTHER: 'Otra gestión',
};
/** Opened by other flows (onboarding, underwriting files), never from the new-order form. */
const KIND_AUTOMATIC = ['REGULATOR_FILING', 'UNDERWRITING', 'APPOINTMENT'];

// Movements on group policies (colectivas): each kind asks for what the carrier needs.
type GroupMemberView = {
  name: string | null;
  nationalId: string | null;
  birthDate: string | null;
  plate: string | null;
  vehicle: string | null;
  certificate: string | null;
  sumInsured: Money | null;
};
type GroupMovementView = {
  insured: 'PERSON' | 'VEHICLE';
  groupPolicy: string | null;
  policyholder: string | null;
  effectiveDate: string;
  members: GroupMemberView[];
  documents: string[];
};
const GROUP_KINDS = ['GROUP_ISSUE', 'GROUP_INCLUSION', 'GROUP_EXCLUSION', 'GROUP_CANCELLATION'];
/** The lines a group policy is written in (people in health, vehicles in autos). */
const GROUP_LINES: Record<string, 'PERSON' | 'VEHICLE'> = { HEALTH: 'PERSON', AUTO: 'VEHICLE' };
/** An exclusion or a cancellation names the certificates that leave the group. */
const leavesGroup = (kind: string) => kind === 'GROUP_EXCLUSION' || kind === 'GROUP_CANCELLATION';
/** One insured of the movement: what to fill depends on the kind and on people or vehicles. */
const memberRow = (kind: string, vehicles: boolean, i: number) => {
  const input = (key: string, label: string, attrs: Parameters<typeof textControl>[0] = {}) =>
    field(label, textControl({ 'data-member-field': key, ...attrs }));
  const who = vehicles
    ? `${input('plate', leavesGroup(kind) ? 'Placa (opcional)' : 'Placa', { class: 'mono', maxlength: 20, placeholder: 'P-123ABC' })}${leavesGroup(kind) ? '' : input('vehicle', 'Vehículo', { maxlength: 160, placeholder: 'Toyota Hilux 2024' })}`
    : `${input('name', leavesGroup(kind) ? 'Nombre (opcional)' : 'Nombre del asegurado', { maxlength: 160, placeholder: 'Lucía Pérez' })}${leavesGroup(kind) ? '' : `${input('nationalId', 'DPI (opcional)', { class: 'mono', maxlength: 20, inputmode: 'numeric' })}${input('birthDate', 'Fecha de nacimiento (opcional)', { type: 'date' })}`}`;
  const sum = leavesGroup(kind)
    ? ''
    : input('sumInsured', vehicles ? 'Suma asegurada' : 'Suma asegurada (opcional)', {
        class: 'num',
        inputmode: 'decimal',
        placeholder: vehicles ? '185,000.00' : 'La del plan',
      });
  const certificate = leavesGroup(kind)
    ? input('certificate', 'Certificado', { class: 'mono', maxlength: 40, placeholder: '0042' })
    : '';
  return `<div class="member-row" data-member role="group" aria-label="${vehicles ? 'Vehículo' : 'Asegurado'} ${i + 1}">${certificate}${who}${sum}</div>`;
};
/** Shows the fields of the chosen kind of order; rows already typed are kept by count. */
function syncGroupForm() {
  const kind = ($('#o-kind') as HTMLSelectElement | null)?.value ?? '';
  const group = GROUP_KINDS.includes(kind);
  const what = $('#o-what') as HTMLElement | null;
  if (what) what.hidden = group;
  for (const el of document.querySelectorAll<HTMLElement>('[data-group-only]')) el.hidden = !group;
  if (!group) return;
  const vehicles = GROUP_LINES[($('#o-group-line') as HTMLSelectElement).value] === 'VEHICLE';
  const issue = kind === 'GROUP_ISSUE';
  ($('#o-group-policy-field') as HTMLElement).hidden = issue;
  ($('#o-group-holder-field') as HTMLElement).hidden = !issue;
  const rows = $('#o-members') as HTMLElement;
  const n = Math.max(1, rows.querySelectorAll('[data-member]').length);
  rows.innerHTML = Array.from({ length: n }, (_, i) => memberRow(kind, vehicles, i)).join('');
  ($('#o-members-legend') as HTMLElement).textContent = vehicles
    ? leavesGroup(kind)
      ? 'Certificados de los vehículos que salen'
      : 'Vehículos'
    : leavesGroup(kind)
      ? 'Certificados de los asegurados que salen'
      : 'Asegurados';
  ($('#o-member-add') as HTMLElement).textContent = vehicles
    ? 'Agregar otro vehículo'
    : 'Agregar otro asegurado';
  ($('#o-members-hint') as HTMLElement).textContent = issue
    ? 'En una emisión puedes listar a los asegurados o solo adjuntar su lista en los documentos.'
    : '';
}
/** The movement typed in the new-order form (the API and the domain check what each kind needs). */
function groupMovementInput(kind: string): Record<string, unknown> {
  const line = ($('#o-group-line') as HTMLSelectElement).value;
  const value = (sel: string) => ($(sel) as HTMLInputElement).value.trim();
  const effectiveDate = value('#o-group-date');
  if (!effectiveDate) throw new InputError('Falta la fecha de efecto del movimiento.');
  const members = [...document.querySelectorAll<HTMLElement>('#o-members [data-member]')]
    .map((row) => {
      const v = (key: string) =>
        row.querySelector<HTMLInputElement>(`[data-member-field="${key}"]`)?.value.trim() || null;
      const sum = v('sumInsured');
      const cents = sum === null ? null : centsOf(sum, false);
      return {
        name: v('name'),
        nationalId: v('nationalId'),
        birthDate: v('birthDate'),
        plate: v('plate'),
        vehicle: v('vehicle'),
        certificate: v('certificate'),
        sumInsured: cents ? { amountMinor: cents, currency: 'GTQ' } : null,
      };
    })
    .filter((m) => Object.values(m).some((x) => x !== null));
  return {
    insured: GROUP_LINES[line] ?? 'PERSON',
    groupPolicy: kind === 'GROUP_ISSUE' ? null : value('#o-group-policy') || null,
    policyholder: kind === 'GROUP_ISSUE' ? value('#o-group-holder') || null : null,
    effectiveDate,
    members,
    documents: ($('#o-group-docs') as HTMLTextAreaElement).value
      .split('\n')
      .map((d) => d.trim())
      .filter(Boolean),
  };
}
/** «Colectiva QZ-GM-COL-001 · efecto 1 nov 2026» and who it is about, for the order's card. */
const movementText = (m: GroupMovementView) => {
  const who = m.members.map((x) =>
    [
      x.certificate ? `Certificado ${x.certificate}` : '',
      x.name ?? '',
      x.plate ? `${x.plate}${x.vehicle ? ` (${x.vehicle})` : ''}` : '',
      x.sumInsured ? `suma ${fmt(x.sumInsured)}` : '',
    ]
      .filter(Boolean)
      .join(' · '),
  );
  return `<div class="order-detail movement"><p><strong>${m.groupPolicy ? `Colectiva <span class="mono">${esc(m.groupPolicy)}</span>` : `Contratante ${esc(m.policyholder ?? '')}`}</strong> · ${m.insured === 'VEHICLE' ? 'vehículos' : 'personas'} · efecto ${esc(date(m.effectiveDate))}</p>${who.length ? `<ul>${who.map((w) => `<li>${esc(w)}</li>`).join('')}</ul>` : ''}${m.documents.length ? `<p class="muted small">Documentos: ${esc(m.documents.join(', '))}</p>` : ''}</div>`;
};
/** The agent's tax regime, as the agency says it. */
const REGIME: Record<string, string> = {
  SIMPLIFIED: 'Régimen opcional simplificado',
  PROFITS: 'Régimen sobre las utilidades',
  SMALL_TAXPAYER: 'Pequeño contribuyente',
};
/** "LIFE: 65" → "Vida 65 %". */
const sharesText = (commissions: Record<string, string>) =>
  Object.entries(commissions)
    .map(([line, share]) => `${PRODUCT[line] ?? line} ${share} %`)
    .join(' · ');
const hoursText = (h: number) =>
  h < 24 ? `${Math.round(h)} h` : `${Math.round(h / 24)} d ${Math.round(h % 24)} h`;

/** The orders on screen (completing one reads what it is about: kind, subject, currency). */
let currentOrders: DeskTaskView[] = [];
/** The numbers of every order the person sees (GET /v1/desk-tasks/summary). */
type DeskSummary = {
  open: number;
  waitingAgency: number;
  late: number;
  done: number;
  rejected: number;
};

export async function ordenes() {
  const [taskPages, summary, carriers] = await Promise.all([
    paged<DeskTaskView>('ordenes', '/v1/desk-tasks'),
    // The numbers count every order, not only the pages loaded.
    api<DeskSummary>('GET', '/v1/desk-tasks/summary'),
    api<CarrierView[]>('GET', '/v1/carriers'),
  ]);
  const tasks = taskPages.items;
  currentOrders = tasks;
  const manage = supervises();
  let html = pageHead(
    'Órdenes de trabajo',
    'Lo que las aseguradoras trabajan a mano (productos sin cotizador o emisor en línea, endosos, cambios): en qué etapa va, quién responde y cuánto falta frente al tiempo acordado.',
  );
  html += `<section class="kpis" aria-label="Órdenes">
    <div class="card kpi"><span class="kpi-label">${icon('inbox')}Abiertas</span><span class="kpi-value">${summary.open}</span><span class="kpi-foot">${summary.waitingAgency} esperan información de la agencia</span></div>
    <div class="card kpi ${summary.late ? 'warn' : ''}"><span class="kpi-label">${icon('warn')}Fuera de tiempo</span><span class="kpi-value">${summary.late}</span><span class="kpi-foot">${summary.late ? 'pasaron el tiempo acordado' : 'Todo en tiempo'}</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('ok')}Completadas</span><span class="kpi-value">${summary.done}</span><span class="kpi-foot">${summary.rejected} rechazadas o retiradas</span></div>
  </section>`;
  html += `<form id="order-form" class="card" novalidate><div class="card-head"><h2>Pedir una orden a la aseguradora</h2></div>
    <div class="form-grid">
      <fieldset><legend>Aseguradora</legend>
        ${field(
          'Aseguradora',
          selectControl(
            { id: 'o-carrier' },
            carriers.map((c) => ({ value: c.id, label: c.name })),
          ),
        )}
        ${field(
          'Tipo',
          selectControl(
            { id: 'o-kind' },
            `${Object.entries(KIND)
              .filter(([k]) => !KIND_AUTOMATIC.includes(k) && !GROUP_KINDS.includes(k))
              .map(([k, v]) => optionTag(k, v))
              .join(
                '',
              )}<small class="option-group">Pólizas colectivas</small>${GROUP_KINDS.map((k) => optionTag(k, KIND[k] ?? k)).join('')}`,
          ),
        )}
      </fieldset>
      <fieldset id="o-what"><legend>Qué se pide</legend>
        ${field('Título', textControl({ id: 'o-title', placeholder: 'Emitir vida para Pedro Gómez', maxlength: 160 }))}
        ${field('Póliza (si aplica)', textControl({ id: 'o-policy', placeholder: 'QZ-VIDA-2026-000123' }))}
      </fieldset>
      <fieldset data-group-only hidden><legend>Colectiva</legend>
        ${field(
          'Ramo',
          selectControl(
            { id: 'o-group-line' },
            Object.keys(GROUP_LINES).map((l) => ({ value: l, label: PRODUCT[l] ?? l })),
          ),
        )}
        <div class="field" id="o-group-policy-field">${textControl({ label: 'Póliza colectiva', id: 'o-group-policy', class: 'mono', maxlength: 60, placeholder: 'QZ-GM-COL-001' })}</div>
        <div class="field" id="o-group-holder-field" hidden>${textControl({ label: 'Contratante', id: 'o-group-holder', maxlength: 160, placeholder: 'Transportes Gómez, S.A.' })}</div>
        ${field('Fecha de efecto', dateControl({ id: 'o-group-date' }))}
      </fieldset>
      <fieldset><legend>Detalle</legend>
        ${field('Datos para la aseguradora', textareaControl({ id: 'o-detail', rows: 4, placeholder: 'Suma asegurada, forma de pago, documentos adjuntos…' }))}
        <div class="field" data-group-only hidden>${textareaControl({ label: 'Documentos que se envían (uno por línea)', id: 'o-group-docs', rows: 3, placeholder: 'solicitud-inclusion.pdf\ndpi-lucia-perez.pdf' })}</div>
      </fieldset>
      <fieldset class="group-members" data-group-only hidden><legend id="o-members-legend">Asegurados</legend>
        <div id="o-members"></div>
        <div class="order-buttons"><button type="button" class="btn sm" id="o-member-add" data-member-add>Agregar otro asegurado</button><span class="muted small" id="o-members-hint"></span></div>
      </fieldset>
    </div>
    <div class="form-foot"><span class="muted small">El tiempo acordado depende de la aseguradora y del tipo de orden.</span>
      <button class="btn primary" type="submit" id="order-submit">Crear orden</button></div></form>`;
  if (!tasks.length)
    return (
      html +
      empty(
        'inbox',
        'No hay órdenes todavía',
        'Cuando una aseguradora deba trabajar algo a mano, crea aquí la orden y sigue su avance.',
      )
    );
  const actions = (t: DeskTaskView) => {
    if (t.closedAt) return '';
    /** The carrier's reference typed on completing (required when `required` says why). */
    const reference = (placeholder: string, required?: string) =>
      textControl({
        class: 'mono',
        'data-order-reference': t.id,
        'data-required': required,
        placeholder,
      });
    const b = (to: string, label: string, cls = '') =>
      `<button type="button" class="btn sm ${cls}" data-order="${esc(t.id)}" data-to="${to}">${label}</button>`;
    const out: string[] = [];
    if (t.stage === 'REQUESTED') {
      if (manage) out.push(b('SENT_TO_CARRIER', 'Enviar a la aseguradora', 'primary'));
      out.push(b('REJECTED', 'Retirar', 'danger'));
    } else if (t.stage === 'WAITING_AGENCY') {
      out.push(b('IN_PROGRESS', 'Responder a la aseguradora', 'primary'));
      if (manage) out.push(b('REJECTED', 'Rechazada', 'danger'));
    } else if (manage) {
      if (t.stage === 'SENT_TO_CARRIER') out.push(b('IN_PROGRESS', 'La trabaja'));
      out.push(b('WAITING_AGENCY', 'Pide información'));
      out.push(b('DONE', 'Completada', 'primary'));
      out.push(b('REJECTED', 'Rechazada', 'danger'));
    }
    if (!out.length) return '';
    return `<div class="toolbar order-actions">
      ${field('Nota', textControl({ 'data-order-note': t.id, placeholder: 'Qué pasó o qué se envía' }))}
      ${manage && t.stage !== 'REQUESTED' && t.kind === 'REGULATOR_FILING' ? field('Número de registro SIB', reference('Al completar: SIB-AG-…', 'Escribe el número de registro que dio la SIB.')) : ''}
      ${
        manage && t.stage !== 'REQUESTED' && t.kind === 'APPOINTMENT'
          ? `${field('Código que creó la aseguradora', reference('Al completar: rosa', 'Escribe el código que creó la aseguradora.'))}
      ${(t.request?.productLines ?? [])
        .map((l) =>
          field(
            `Comisión del agente en ${esc(PRODUCT[l] ?? l)} (%)`,
            textControl({
              class: 'num',
              'data-order-share': t.id,
              'data-line': l,
              inputmode: 'decimal',
              placeholder: '65',
            }),
          ),
        )
        .join('')}
      ${field(
        'Régimen del agente',
        selectControl(
          { 'data-order-regime': t.id },
          Object.entries(REGIME).map(([k, v]) => ({ value: k, label: v })),
        ),
      )}`
          : ''
      }
      ${manage && t.stage !== 'REQUESTED' && t.kind === 'GROUP_ISSUE' ? field('Número de la póliza colectiva', reference('Al completar: QZ-GM-COL-…', 'Escribe el número de la póliza colectiva que emitió la aseguradora.')) : ''}
      ${manage && t.stage !== 'REQUESTED' && GROUP_KINDS.includes(t.kind) && t.kind !== 'GROUP_ISSUE' ? field('Referencia de la aseguradora', reference('Al completar (opcional)')) : ''}
      ${manage && t.stage !== 'REQUESTED' && t.subject?.type === 'ISSUANCE' ? field('Número de póliza', reference('Al completar: el número que emitió', 'Escribe el número de póliza que dio la aseguradora.')) : ''}
      ${manage && t.stage !== 'REQUESTED' && t.subject?.type === 'ISSUANCE' && t.request?.issuance?.premium ? firmPremiumFields(t.id, t.request.issuance.premium.gross, !!t.request.issuance.ruleSet) : ''}
      ${manage && t.stage !== 'REQUESTED' && t.automatic && t.subject?.type !== 'ISSUANCE' && !followsPolicy(t) ? field('Referencia de la aseguradora', reference('Al completar (opcional)')) : ''}
      ${manage && t.stage !== 'REQUESTED' && followsPolicy(t) === 'ENDORSE' ? `${field('Referencia del endoso', reference('Al completar: END-…', 'Escribe la referencia del endoso que dio la aseguradora.'))}${field('Prima total del endoso', textControl({ class: 'num', 'data-order-total': t.id, inputmode: 'decimal', placeholder: '112.00 (negativa si devuelve)' }))}${field('Prima neta del endoso', textControl({ class: 'num', 'data-order-net': t.id, inputmode: 'decimal', placeholder: '100.00' }))}` : ''}
      ${manage && t.stage !== 'REQUESTED' && followsPolicy(t) === 'CANCEL' ? `${field('Referencia de la anulación', reference('Al completar: ANU-…', 'Escribe la referencia de la anulación que dio la aseguradora.'))}${field('Comisión que devuelve (opcional)', textControl({ class: 'num', 'data-order-commission': t.id, inputmode: 'decimal', placeholder: 'Si la aseguradora la indica' }))}` : ''}
      ${
        manage && t.stage !== 'REQUESTED' && t.kind === 'UNDERWRITING'
          ? `${field(
              'Resolución al completar',
              selectControl({ 'data-order-decision': t.id }, [
                { value: 'ACCEPT', label: 'Acepta el riesgo' },
                { value: 'DECLINE', label: 'Declina el riesgo' },
              ]),
            )}
      ${field('Recargo en % (opcional)', textControl({ 'data-order-surcharge': t.id, inputmode: 'decimal', placeholder: '15' }))}
      ${field('Exclusiones (opcional)', textControl({ 'data-order-exclusions': t.id, placeholder: 'Separadas por punto y coma' }))}`
          : ''
      }
      ${manage && t.stage !== 'REQUESTED' ? field('Responsable', textControl({ 'data-order-assignee': t.id, placeholder: person(t.assignee) })) : ''}
      <div class="order-buttons">${out.join('')}</div></div>`;
  };
  const cards = tasks
    .map((t) => {
      const ratio = Math.min(1, t.elapsedHours / t.slaHours);
      const due = t.closedAt
        ? `${t.overdueHours ? 'Cerrada fuera de tiempo' : 'Cerrada en tiempo'} · ${hoursText(t.elapsedHours)}`
        : t.overdueHours
          ? `<strong style="color:var(--bad)">Vencida hace ${hoursText(t.overdueHours)}</strong>`
          : `Vence ${esc(when(t.dueAt))} · faltan ${hoursText(t.slaHours - t.elapsedHours)}`;
      return `<article class="card"><div class="card-head">
        <div class="policy-title"><strong>${esc(t.title)}</strong>
          <div class="policy-meta"><span>${esc(carrierName(t.carrier))}</span><span>${esc(KIND[t.kind] ?? t.kind)}</span>${t.policyNumber ? `<span class="mono">${esc(t.policyNumber)}</span>` : ''}${t.reference ? `<span>Resultado <b class="mono">${esc(t.reference)}</b></span>` : ''}${t.subject?.type === 'ONBOARDING' ? `<span>Onboarding <b class="mono">${esc(t.subject.id)}</b></span>` : ''}${t.agentTerms ? `<span>Código <b class="mono">${esc(t.agentTerms.agentId)}</b> · ${esc(sharesText(t.agentTerms.commissions))}</span>` : ''}${t.subject?.type === 'UNDERWRITING_FILE' ? `<span>Expediente <b class="mono">${esc(t.subject.id)}</b></span>` : ''}${t.resolution ? `<span>${t.resolution.decision === 'ACCEPT' ? `Aceptado${t.resolution.surchargePercent ? ` con recargo de ${esc(t.resolution.surchargePercent)} %` : ''}${t.resolution.exclusions.length ? ` · excluye ${esc(t.resolution.exclusions.join(', '))}` : ''}` : 'Declinado'}</span>` : ''}${t.firmPremium ? `<span>${esc(firmPremiumText(t.firmPremium))}</span>` : ''}${t.automatic ? '<span>Abierta por Nubi: sin integración</span>' : ''}${t.agentId ? `<span>Agente <b>${esc(cap(t.agentId))}</b></span>` : ''}<span>A cargo de <b>${esc(person(t.assignee))}</b></span></div></div>
        <span class="spacer"></span>
        <span class="progress-text">${meter(ratio, `inline ${t.overdueHours ? 'late' : ''}`)}${due}</span>
        ${pill(t.stage)}</div>
        ${t.movement ? movementText(t.movement) : ''}
        ${t.detail ? `<p class="order-detail">${esc(t.detail)}</p>` : ''}
        <ol class="timeline">${t.history
          .map(
            (h) => `<li><span class="pill ${esc(h.stage)}">${esc(STATUS[h.stage] ?? h.stage)}</span>
            <span class="muted small">${esc(when(h.at))} · ${esc(person(h.by))}${h.assignee ? ` → ${esc(person(h.assignee))}` : ''}</span>${h.note ? `<span class="timeline-note">${esc(h.note)}</span>` : ''}</li>`,
          )
          .join('')}</ol>
        ${actions(t)}</article>`;
    })
    .join('');
  return `${html}<section class="policies">${cards}</section>${loadMore('ordenes', taskPages.more, tasks.length, 'órdenes')}`;
}

/** Forgets the orders on screen. */
export function resetOrders() {
  currentOrders = [];
}

export const actions: Actions = {
  click: {
    memberAdd: () => {
      const rows = $('#o-members') as HTMLElement;
      const kind = ($('#o-kind') as HTMLSelectElement).value;
      const vehicles = GROUP_LINES[($('#o-group-line') as HTMLSelectElement).value] === 'VEHICLE';
      const n = rows.querySelectorAll('[data-member]').length;
      rows.insertAdjacentHTML('beforeend', memberRow(kind, vehicles, n));
      rows.querySelector<HTMLInputElement>('[data-member]:last-child wa-input')?.focus();
    },
    order: (t, d) => {
      if (!d.order || !d.to) return;
      const id = d.order;
      const note = document
        .querySelector<HTMLInputElement>(`[data-order-note="${CSS.escape(id)}"]`)
        ?.value.trim();
      const assignee = document
        .querySelector<HTMLInputElement>(`[data-order-assignee="${CSS.escape(id)}"]`)
        ?.value.trim();
      const referenceInput = document.querySelector<HTMLInputElement>(
        `[data-order-reference="${CSS.escape(id)}"]`,
      );
      const reference = referenceInput?.value.trim();
      const decision = document.querySelector<HTMLSelectElement>(
        `[data-order-decision="${CSS.escape(id)}"]`,
      )?.value;
      const surchargeText =
        document
          .querySelector<HTMLInputElement>(`[data-order-surcharge="${CSS.escape(id)}"]`)
          ?.value.trim()
          .replace(',', '.') ?? '';
      const exclusions = (
        document
          .querySelector<HTMLInputElement>(`[data-order-exclusions="${CSS.escape(id)}"]`)
          ?.value.split(';') ?? []
      )
        .map((x) => x.trim())
        .filter(Boolean);
      const orderValue = (attr: string) =>
        document.querySelector<HTMLInputElement>(`[data-order-${attr}="${CSS.escape(id)}"]`)
          ?.value ?? '';
      const orderCurrency = currentOrders.find((o) => o.id === id)?.request?.currency ?? 'GTQ';
      const shares = [
        ...document.querySelectorAll<HTMLInputElement>(`[data-order-share="${CSS.escape(id)}"]`),
      ].map((input) => [input.dataset.line ?? '', input.value.trim().replace(',', '.')] as const);
      const regime = document.querySelector<HTMLSelectElement>(
        `[data-order-regime="${CSS.escape(id)}"]`,
      )?.value;
      void act(t, 'Registrando…', async () => {
        const inCurrency = (cents: number | null) =>
          cents === null ? undefined : { amountMinor: cents, currency: orderCurrency };
        const total = inCurrency(centsOf(orderValue('total'), true));
        const net = inCurrency(centsOf(orderValue('net'), true));
        const commission = inCurrency(centsOf(orderValue('commission'), false));
        const firmText = (k: string) => orderValue(`firm-${k}`).trim();
        const firmGiven = d.to === 'DONE' && FIRM_FIELDS.some(([k]) => firmText(k));
        const firmMoney = (k: string, label: string) => {
          const cents = centsOf(firmText(k), false);
          if (cents === null) throw new InputError(`Escribe ${label} de la prima en firme.`);
          return { amountMinor: cents, currency: orderCurrency };
        };
        const firmCount = Number(firmText('count'));
        if (firmGiven && !(Number.isInteger(firmCount) && firmCount >= 1 && firmCount <= 12))
          throw new InputError('Escribe el número de recibos de la prima en firme: de 1 a 12.');
        const firmPremium = firmGiven
          ? {
              net: firmMoney('net', 'la prima neta'),
              issuanceFee: firmMoney('fee', 'los gastos de emisión'),
              installmentSurcharge: firmMoney('surcharge', 'el recargo por fraccionamiento'),
              vat: firmMoney('vat', 'el IVA'),
              gross: firmMoney('gross', 'la prima total'),
              installments: {
                count: firmCount,
                first: firmMoney('first', 'el primer recibo'),
                regular: firmMoney('regular', 'cada recibo'),
                last: firmMoney('last', 'el último recibo'),
              },
            }
          : null;
        const followsOrder = currentOrders.find((o) => o.id === id);
        const follows = followsOrder ? followsPolicy(followsOrder) : null;
        if (d.to === 'DONE' && follows === 'ENDORSE' && (!total || !net))
          throw new InputError(
            'Escribe la prima total y la prima neta del endoso, como la cobró la aseguradora.',
          );
        const amounts =
          d.to === 'DONE' && (total || net || commission)
            ? {
                ...(total ? { total } : {}),
                ...(net ? { net } : {}),
                ...(commission ? { commission } : {}),
              }
            : null;
        const surcharge = surchargeText ? Number(surchargeText) : null;
        if (surcharge !== null && !(surcharge > 0 && surcharge <= 500))
          throw new InputError('El recargo es un porcentaje mayor que 0, por ejemplo 15.');
        if ((d.to === 'WAITING_AGENCY' || d.to === 'REJECTED') && !note)
          throw new InputError('Escribe en la nota qué pide o por qué se rechaza.');
        if (d.to === 'DONE' && referenceInput?.dataset.required && !reference)
          throw new InputError(referenceInput.dataset.required);
        const appointmentTerms = d.to === 'DONE' && shares.length > 0;
        if (appointmentTerms && !shares.every(([, v]) => Number(v) > 0 && Number(v) <= 100))
          throw new InputError(
            'Escribe la comisión del agente en cada ramo: más de 0 y hasta 100 %, por ejemplo 65.',
          );
        const r = await api<DeskTaskView>(
          'POST',
          `/v1/desk-tasks/${encodeURIComponent(id)}/advance`,
          {
            to: d.to,
            ...(note ? { note } : {}),
            ...(assignee ? { assignee } : {}),
            ...(d.to === 'DONE' && reference ? { reference } : {}),
            ...(amounts ? { amounts } : {}),
            ...(firmPremium ? { firmPremium } : {}),
            ...(appointmentTerms
              ? {
                  agentTerms: {
                    commissions: Object.fromEntries(shares),
                    regime: regime ?? 'SIMPLIFIED',
                  },
                }
              : {}),
            ...(d.to === 'DONE' && decision
              ? {
                  resolution: {
                    decision,
                    surchargePercent: decision === 'ACCEPT' ? surcharge : null,
                    exclusions: decision === 'ACCEPT' ? exclusions : [],
                  },
                }
              : {}),
          },
        );
        await render(current, { quiet: true });
        if (r.subject?.type === 'UNDERWRITING_FILE' && r.stage === 'DONE')
          return r.resolution?.decision === 'ACCEPT'
            ? `Resolución anotada en el expediente ${r.subject.id}: la aseguradora acepta; ya se puede emitir.`
            : `Resolución anotada en el expediente ${r.subject.id}: la aseguradora declina el riesgo.`;
        if (r.kind === 'APPOINTMENT' && r.stage === 'DONE')
          return `Código ${r.agentTerms?.agentId ?? r.reference ?? ''} anotado: sus pólizas y comisiones sin liquidar pasan a ese código.`;
        if (r.subject?.type === 'ONBOARDING' && (r.stage === 'DONE' || r.stage === 'REJECTED'))
          return r.stage === 'DONE'
            ? `Registro ${r.reference ?? ''} anotado en el onboarding de la agente.`
            : 'Rechazo anotado en el onboarding; puede volver a tramitarse.';
        if (r.subject?.type === 'ISSUANCE' && r.stage === 'DONE')
          return r.firmPremium
            ? `Póliza ${r.reference ?? ''} registrada. ${firmPremiumText(r.firmPremium)}.`
            : `Póliza ${r.reference ?? ''} registrada con su plan de recibos.`;
        if (followsPolicy(r) === 'ENDORSE' && r.stage === 'DONE')
          return `Endoso ${r.reference ?? ''} registrado: su prima se cobra según lo que acordó la aseguradora.`;
        if (followsPolicy(r) === 'CANCEL' && r.stage === 'DONE')
          return `Anulación ${r.reference ?? ''} registrada: los recibos pendientes desde esa fecha se anularon.`;
        return `Orden en «${STATUS[r.stage] ?? r.stage}».`;
      });
    },
  },
  submit: {
    'order-form': (ev) => {
      ev.preventDefault();
      void act($('#order-submit'), 'Creando la orden…', async () => {
        const kind = ($('#o-kind') as HTMLSelectElement).value;
        const group = GROUP_KINDS.includes(kind);
        const detail = ($('#o-detail') as HTMLTextAreaElement).value.trim();
        const t = await api<DeskTaskView>(
          'POST',
          '/v1/desk-tasks',
          group
            ? {
                carrier: ($('#o-carrier') as HTMLSelectElement).value,
                kind,
                detail,
                productLine: ($('#o-group-line') as HTMLSelectElement).value,
                movement: groupMovementInput(kind),
              }
            : {
                carrier: ($('#o-carrier') as HTMLSelectElement).value,
                kind,
                title: textInput('#o-title', 'el título de la orden'),
                detail,
                policyNumber: ($('#o-policy') as HTMLInputElement).value.trim() || null,
              },
        );
        await render(current, { quiet: true });
        return `Orden ${t.id} creada; vence ${when(t.dueAt)} (${t.slaHours} h acordadas).`;
      });
    },
  },
  change: {
    // The fields of the chosen kind (a group policy asks for its insured).
    'o-kind': syncGroupForm,
    'o-group-line': syncGroupForm,
  },
};
